import { useState, useEffect, useRef, useMemo } from "react";
import { ChevronDownIcon } from "../../icons";
import Spinner from "../ui/Spinner/Spinner";
import PayBankDetailsModal from "../ui/modal/PayBankDetailsModal";
import ReceiveBankDetailsModal from "../ui/modal/ReceiveBankDetailsModal";
import ConfirmationModal from "../ui/modal/ConfirmationModal";
import { fetchExchangeRate, createExchangeOrder, fetchTransactionFee } from "./Apis/exchangeRates";
import {
  ExchangeRequest,
  ExchangeRateResponse,
  ExchangeOrder,
  Currency,
  PaymentDetails,
  // SupportedCurrency,
  Wallet,
} from "./types/Exchange";
import { useParams } from "react-router";
import { cache, truncateAddress, validateBusinessAccountName } from "../../utils";
import { SelectMethodTypeModal } from "../ui/modal/SelectMethodTypeModal";
import { WalletSelectionModal } from "../ui/modal/WalletSelectionModal";
import {
  CONFIG_TYPE,
  EMAIL_PAYMENT_METHODS,
  INTERNATIONAL_BANK_ISO2,
  isBankTransferMethod,
  MOMO_COUNTRIES_ISO2,
  PAYMENT_METHOD,
  PAYMENT_METHOD_ARRAY,
  selectMethodTypes,
} from "../../utils/constant";
import {
  autoClearError,
  FormatNumber,
  to2DecimalPlace,
  validatePaymentDetails,
} from "../../utils/helpers";
import { useCurrencyStore } from "../../stores/useCurrencyStore";
import { useOrdersStore } from "../../stores/useOrderStore";
import AutoProcessingModal from "../ui/modal/AutoProcessingModal";

interface USER {
  businessName: string;
}

export default function Exchange() {
  const [amount, setAmount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [payFrom, setPayFrom] = useState("");
  const [receiveTo, setReceiveTo] = useState("");
  const [showPayModal, setShowPayModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [showAutoProcessingModal, setShowAutoProcessingModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState(120);
  const [fromCurrency, setFromCurrency] = useState<Currency | null>(null);
  const [toCurrency, setToCurrency] = useState<Currency | null>(null);
  const [exchangeRate, setExchangeRate] = useState<ExchangeRateResponse | null>(
    null
  );
  const [narration, setNarration] = useState("");
  const [order, setOrder] = useState<ExchangeOrder | null>(null);
  const [paymentDetails, setPaymentDetails] = useState({
    debit: {} as PaymentDetails,
    credit: {} as PaymentDetails,
  });
  const { offerId } = useParams<{ offerId: string }>();
  const [error, setError] = useState("");
  const [apiMessage, setApiMessage] = useState("");
  const [bdcOfferId, setBdcOfferId] = useState("");
  const [showFromDropdown, setShowFromDropdown] = useState(false);
  const [showToDropdown, setShowToDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [storedAccountName, setStoredAccountName] = useState("");
  const [accountNameError, setAccountNameError] = useState("");

  // ---- New state for the selection flow
  const [showMethodTypeModal, setShowMethodTypeModal] = useState(false);
  const [methodTypeContext, setMethodTypeContext] = useState<"PAY" | "RECEIVE">(
    "PAY"
  );
  const [showWalletModal, setShowWalletModal] = useState(false);
  const emptyPaymentDetails: PaymentDetails = {
    accountName: "",
    accountNumber: "",
    paymentMethod: "",
    iso2: "",
    currency: "",
    providerName: "",
  };

  const [fee, setFee] = useState<number>(0);
  const [isLoadingFee, setIsLoadingFee] = useState(false);
  const [feeApplies, setFeeApplies] = useState(false);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [flowPayMethods, setFlowPayMethods] =
    useState<string[]>(PAYMENT_METHOD_ARRAY);
  const [flowReceiveMethods, setFlowReceiveMethods] =
    useState<string[]>(PAYMENT_METHOD_ARRAY);

  // const { currencies, hasLoaded, loadingCurrency } = useCurrencyStore();
  const { currencies: rawCurrencies, hasLoaded, loadingCurrency } = useCurrencyStore();

// const currencies = useMemo(() => {
//   const seen = new Set<string>();
//   return rawCurrencies.filter((c) => {
//     if (seen.has(c.code)) return false;
//     seen.add(c.code);
//     return true;
//   });
// }, [rawCurrencies]);
const currencies = rawCurrencies;
  const { loadOrders } = useOrdersStore();
  const [fromCurrencySearch, setFromCurrencySearch] = useState("");
const [toCurrencySearch, setToCurrencySearch] = useState("");

  // ---- Close dropdown when clicking outside for the currencies fetched
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowFromDropdown(false);
        setShowToDropdown(false);
        setFromCurrencySearch("");
        setToCurrencySearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ---- Fetch the user details for validation of business name
  useEffect(() => {
    // Fetch User from cache for validation of inputted account business Name.
    const fetchUser = () => {
      const cachedUser = cache.get("USER") as USER;
      if (cachedUser) {
        const checkedName = cachedUser.businessName;
        setStoredAccountName(checkedName);
      }
    };

    fetchUser();
  }, []);

  useEffect(() => {
    if (hasLoaded && currencies.length > 0) {
      const defaultFrom =
        currencies.find((c) => c.code === "NGN") || currencies[0];
      setFromCurrency(defaultFrom);

      const defaultTo =
        currencies.find((c) => c.code === "KES") ||
        (currencies.length > 1
          ? currencies.find((c) => c.code !== defaultFrom.code) || currencies[0]
          : currencies[0]);
      setToCurrency(defaultTo);
    }
  }, [hasLoaded, currencies]);

  // ---- Timer effect for the confirmation modal
  useEffect(() => {
    let timer: number;
    if (showConfirmationModal && timeLeft > 0) {
      timer = window.setInterval(() => setTimeLeft((prev) => prev - 1), 1000);
    }
    return () => {
      if (timer) window.clearInterval(timer);
    };
  }, [showConfirmationModal, timeLeft]);

  useEffect(() => {
    if (!showConfirmationModal) {
      setTimeLeft(120);
    }
  }, [showConfirmationModal]);

  useEffect(() => {
    let isMounted = true;

    const fetchRate = async () => {
      if (amount && parseFloat(amount) > 0 && fromCurrency && toCurrency) {
        setIsLoading(true);
        setApiMessage("");
        setError("");
        setExchangeRate(null);

        try {
          const rateData = await fetchExchangeRate({
            fromCurrency: fromCurrency.code,
            toCurrency: toCurrency.code,
            fromCurrencyIso2: fromCurrency.iso2,
            toCurrencyIso2: toCurrency.iso2,
            amount: parseFloat(amount),
          });

          if (!isMounted) return;

          if (!rateData.success) {
            setApiMessage(rateData.message || "Failed to fetch exchange rate");
            setShowDetails(false);
            return;
          }

          // Only update these for successful responses
          setExchangeRate(rateData);
          setShowDetails(true);

          if (rateData.offerId) {
            setBdcOfferId(rateData.offerId);
          }

          setApiMessage("");
        } catch (error) {
          if (!isMounted) return;
          setError((error as Error).message);
          setShowDetails(false);
        } finally {
          if (isMounted) setIsLoading(false);
        }
      } else {
        setShowDetails(false);
        setApiMessage("");
      }
    };

    const debounceTimer = setTimeout(fetchRate, 500);

    setPayFrom("");
    setReceiveTo("");
    setFee(0);
    setFeeApplies(false);
    setWalletBalance(null);

    return () => {
      isMounted = false;
      clearTimeout(debounceTimer);
    };
  }, [amount, fromCurrency, toCurrency]);

  // ---- Handle amount change when inputtin
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let rawValue = e.target.value.replace(/,/g, ""); // strip commas first

    // Allow only numbers and decimals
    if (/^\d*\.?\d*$/.test(rawValue) || rawValue === "") {
      setAmount(rawValue);
      setError("");
      setApiMessage("");
      setShowDetails(false);
    }
  };

  // ---- New handlers for the selection flow
  const handlePayFromClick = () => {
    setMethodTypeContext("PAY");
    setShowMethodTypeModal(true);
  };

  const handleReceiveToClick = () => {
    setMethodTypeContext("RECEIVE");
    setShowMethodTypeModal(true);
  };

  const handleMethodTypeSelect = (methodType: selectMethodTypes) => {
    setShowMethodTypeModal(false);

    if (methodType === "WALLET") {
      setShowWalletModal(true);
    } else {
      // For other methods, open the appropriate modal with the selected method
      if (methodTypeContext === "PAY") {
        setShowPayModal(true);
        // Store the selected payment method in state if needed
        setPaymentDetails((prev) => ({
          ...prev,
          debit: {
            ...prev.debit,
            paymentMethod: methodType,
          },
        }));
      } else {
        setShowReceiveModal(true);
        setPaymentDetails((prev) => ({
          ...prev,
          credit: {
            ...prev.credit,
            paymentMethod: methodType,
          },
        }));
      }
    }
  };

  const handleWalletSelect = (wallet: Wallet) => {
    setShowWalletModal(false);

    const walletDetails = {
      accountName: wallet.walletName,
      accountNumber: wallet.id,
      providerName: "Wallet",
      paymentMethod: PAYMENT_METHOD.WALLET,
      iso2:
        methodTypeContext === "PAY"
          ? fromCurrency?.iso2 || ""
          : toCurrency?.iso2 || "",
      currency:
        methodTypeContext === "PAY"
          ? fromCurrency?.code || ""
          : toCurrency?.code || "",
      bankCode: "",
      saveDetails: false,
      walletBalance: wallet.balance,
    };

    if (methodTypeContext === "PAY") {
      handleSavePayDetails(walletDetails);
    } else {
      handleSaveReceiveDetails(walletDetails);
    }
  };

  const handleSavePayDetails = (details: PaymentDetails) => {
    if (!fromCurrency) return;

     setReceiveTo("");
  setPaymentDetails((prev) => ({
    ...prev,
    credit: emptyPaymentDetails,
  }));
  setFee(0);
  setIsLoadingFee(false);

    const isWalletPayment = details.paymentMethod === PAYMENT_METHOD.WALLET;

    const fullDetails: PaymentDetails = {
      ...details,
      iso2: fromCurrency.iso2,
      currency: fromCurrency.code,
      ...(!isWalletPayment && {
        ...(details.paymentMethod === PAYMENT_METHOD.MOBILE_MONEY && {
          network: details.providerName,
        }),
        ...(isBankTransferMethod(details.paymentMethod) && {
          bankCode: details.bankCode || "",
          providerName: details.providerName || "",
        }),
      }),
    };

    setPaymentDetails((prev) => ({ ...prev, debit: fullDetails }));

    if (details.paymentMethod !== PAYMENT_METHOD.WALLET) {
      if (
        !validateBusinessAccountName(details.accountName, storedAccountName)
      ) {
        setAccountNameError("Account name must match your business name.");
      } else {
        setAccountNameError("");
      }
    }

    if (isWalletPayment && typeof details.walletBalance === "number") {
      setWalletBalance(details.walletBalance);
    }

    setFeeApplies(isWalletPayment ? true : false);

    setPayFrom(
      isWalletPayment
        ? `Wallet (${details.accountName})`
        : details.paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY
        ? `${truncateAddress(details.walletAddress)}`
        : `${details.accountName}`
    );
// if (details.paymentMethod === PAYMENT_METHOD.WALLET) {
//   // if (fromCurrency?.code === "GHS") {
//   //   setFlowReceiveMethods([PAYMENT_METHOD.WALLET]);
//   // } else {
//     setFlowReceiveMethods(PAYMENT_METHOD_ARRAY);
//   // }
// } else {
//   setFlowReceiveMethods(
//     PAYMENT_METHOD_ARRAY.filter(
//       (method) => method !== PAYMENT_METHOD.WALLET
//     )
//   );
// }

setFlowReceiveMethods(PAYMENT_METHOD_ARRAY);
  };

  // ----  Handle save receive to details from the ReceiveBankDetailsModal
  const handleSaveReceiveDetails = (details: PaymentDetails) => {
    if (!toCurrency) return;

    const isWalletReceipt = details.paymentMethod === PAYMENT_METHOD.WALLET;

    const fullDetails: PaymentDetails = {
      ...details,
      iso2: toCurrency.iso2,
      currency: toCurrency.code,
      ...(!isWalletReceipt && {
        paymentMethod: details.paymentMethod,
        ...(details.paymentMethod === PAYMENT_METHOD.MOBILE_MONEY && {
          network: details.providerName,
        }),
        ...(isBankTransferMethod(details.paymentMethod) && {
          bankCode: details.bankCode || "",
          providerName: details.providerName || "",
        }),
      }),
    };

    setPaymentDetails((prev) => ({ ...prev, credit: fullDetails }));
    setReceiveTo(
      isWalletReceipt
        ? `Wallet (${details.accountName})`
        : details.paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY
        ? `${truncateAddress(details.walletAddress)}`
        : `${details.accountName}`
    );
setFlowPayMethods(PAYMENT_METHOD_ARRAY);
  };

  const hasInsufficientBalance = useMemo(() => {
    const numericAmount = parseFloat(amount);

    return (
      paymentDetails.debit.paymentMethod === PAYMENT_METHOD.WALLET &&
      typeof walletBalance === "number" &&
      !isNaN(numericAmount) &&
      numericAmount > walletBalance
    );
  }, [amount, walletBalance, paymentDetails.debit.paymentMethod]);

  useEffect(() => {
    if (!amount) return;

    setError("");

    const timeout = setTimeout(() => {
      if (hasInsufficientBalance && walletBalance) {
        setError(`Insufficient balance in ${fromCurrency?.code} wallet.`);
      } else {
        setError("");
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [amount, hasInsufficientBalance, fromCurrency?.code]);

  const offerPayMethods = Array.isArray(exchangeRate?.acceptedPaymentMethod)
    ? exchangeRate.acceptedPaymentMethod
    : [exchangeRate?.acceptedPaymentMethod || ""];

  const offerReceiveMethods = Array.isArray(exchangeRate?.payoutPaymentMethod)
    ? exchangeRate.payoutPaymentMethod
    : [exchangeRate?.payoutPaymentMethod || ""];

  const getMethodsAllowedByOfferAndFlow = (
    offerAllowedMethods: string[],
    flowAllowedMethods: string[]
  ) => {
    return offerAllowedMethods.filter((method) =>
      flowAllowedMethods.includes(method)
    );
  };

  const finalPayMethods = getMethodsAllowedByOfferAndFlow(
    offerPayMethods,
    flowPayMethods
  );
  const finalReceiveMethods = getMethodsAllowedByOfferAndFlow(
    offerReceiveMethods,
    flowReceiveMethods
  );

  // ---- handles creating the order
  const handleContinue = async () => {
    const error = !amount
      ? "Please enter an amount"
      : validatePaymentDetails(paymentDetails, "debit") ||
        validatePaymentDetails(paymentDetails, "credit");

    if (error) {
      setError(error);
      return;
    }

    if (!exchangeRate?.paymentDetails) {
      setError("Something went wrong. Please try again.");
      return;
    }

    const actualOfferId = offerId || bdcOfferId;
    if (!actualOfferId) {
      setError("Offer ID is missing");
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const numericAmount = parseFloat(amount);

      if (
        paymentDetails.debit.paymentMethod === PAYMENT_METHOD.WALLET &&
        typeof walletBalance === "number" &&
        numericAmount > walletBalance
      ) {
        setError(
          `Insufficient balance in ${paymentDetails.debit.currency} wallet.`
        );
        autoClearError(setError);
        setIsLoading(false);
        return;
      }
      const buildPaymentDetails = (details: PaymentDetails) => {
        const baseDetails = {
          accountNumber: details.accountNumber,
          accountName: details.accountName,
          iso2: details.iso2,
          currency: details.currency,
        };

        if (details.paymentMethod === PAYMENT_METHOD.WALLET) {
          return {
            ...baseDetails,
            paymentMethod: PAYMENT_METHOD.WALLET,
            walletId: details.accountNumber,
          };
        }

        if (EMAIL_PAYMENT_METHODS.has(details.paymentMethod)) {
          return {
            accountName: details.accountName,
            iso2: details.iso2,
            currency: details.currency,
            paymentMethod: details.paymentMethod,
            email: details.email,
          };
        }

        if (details.paymentMethod === PAYMENT_METHOD.STABLE_CURRENCY) {
          return {
            currency: details.currency,
            iso2: details.iso2,
            paymentMethod: details.paymentMethod,
            walletAddress: details.walletAddress,
            network: details.network,
          };
        }

        if (details.paymentMethod === PAYMENT_METHOD.MOBILE_MONEY) {
          return {
            ...baseDetails,
            network: details.providerName,
            paymentMethod: details.paymentMethod,
            providerName: details.providerName,
          };
        }

        if (details.paymentMethod === PAYMENT_METHOD.WECHAT_PAY || 
    details.paymentMethod === PAYMENT_METHOD.ALIPAY) {
  return {
    accountName: details.accountName,
    iso2: details.iso2,
    currency: details.currency,
    paymentMethod: details.paymentMethod,
    phoneNumber: details.phoneNumber,
  };
}

        if (isBankTransferMethod(details.paymentMethod)) {
          const bankTransferDetails: any = {
            ...baseDetails,
            bankCode: details.bankCode || "",
            paymentMethod: details.paymentMethod || "",
            providerName: details.bankCode || "",
          };

          if (details.iso2 === "NG") {
            bankTransferDetails.providerName = details.providerName;
          } else if (
            details?.iso2 &&
            INTERNATIONAL_BANK_ISO2.has(details.iso2)
          ) {
            bankTransferDetails.bankCode = details.bankCode || "";
            bankTransferDetails.providerName = details.providerName?.trim() ? details.providerName : details.bankCode || "";
          } else if (details?.iso2 && MOMO_COUNTRIES_ISO2.has(details.iso2)) {
            bankTransferDetails.bankCode = details.providerName || "";
            bankTransferDetails.providerName = details.providerName || "";
          } else {
            bankTransferDetails.bankCode =
              details.bankCode || details.providerName || "";
            bankTransferDetails.providerName =
              details.providerName || details.bankCode || "";
          }
          
  if ((details.iso2 === "GB" || details.currency === "EUR") && 
    details.holderCity && details.holderStreet && details.holderPostalCode) {
  bankTransferDetails.holderCity = details.holderCity;
  bankTransferDetails.holderStreet = details.holderStreet;
  bankTransferDetails.holderPostalCode = details.holderPostalCode;
}

          return bankTransferDetails;
        }

        return baseDetails;
      };

      const debitDetails = buildPaymentDetails(paymentDetails.debit);
      const creditDetails = buildPaymentDetails(paymentDetails.credit);

      const orderData: ExchangeRequest = {
        amount: numericAmount,
        buyerDebitPaymentMethod: paymentDetails.debit.paymentMethod || "",
        buyerCreditPaymentMethod: paymentDetails.credit.paymentMethod || "",
        ...(narration.trim() && { narration: narration.trim() }),
      };

      if (paymentDetails.debit.paymentMethod !== PAYMENT_METHOD.WALLET) {
        orderData.buyerDebitPaymentDetails = debitDetails;
      }

      if (paymentDetails.credit.paymentMethod !== PAYMENT_METHOD.WALLET) {
        orderData.buyerCreditPaymentDetails = creditDetails;
      }

      const cleanOrderData = JSON.parse(JSON.stringify(orderData));

      const createdOrder = await createExchangeOrder(
        actualOfferId,
        cleanOrderData
      );

      loadOrders().catch((error) => {
        console.error("Background orders refresh failed:", error);
      });

      const enhancedOrder: ExchangeOrder = {
        ...createdOrder,
        fromCurrency: fromCurrency?.code || "",
        toCurrency: toCurrency?.code || "",
        fromCurrencySymbol: fromCurrency?.symbol || "",
        toCurrencySymbol: toCurrency?.symbol || "",
        buyerDebitPaymentMethod: paymentDetails.debit.paymentMethod || "",
        buyerCreditPaymentMethod: paymentDetails.credit.paymentMethod || "",
        paymentDetailsFromRate: exchangeRate?.paymentDetails,
      };

      setOrder(enhancedOrder);
      const isAutomaticExchange = enhancedOrder.buyerDebitPaymentMethod === PAYMENT_METHOD.WALLET
        // enhancedOrder.buyerDebitPaymentMethod === PAYMENT_METHOD.WALLET &&
        // (enhancedOrder.buyerCreditPaymentMethod ===
        //   PAYMENT_METHOD.MOBILE_MONEY ||
        //   enhancedOrder.buyerCreditPaymentMethod ===
        //     PAYMENT_METHOD.BANK_TRANSFER ||
        //   enhancedOrder.buyerCreditPaymentMethod === PAYMENT_METHOD.WALLET);
      if (isAutomaticExchange) {
        setShowAutoProcessingModal(true);
      } else {
        setShowConfirmationModal(true);
      }
      setTimeLeft(120);
    } catch (error) {
      setError((error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  // ---- fetch currency symbols
  const currencySymbol = () => fromCurrency?.symbol || "";

  // ---- format the display rate to show
  const clampDecimals = (value: number, places = 6) => {
    const [before, after] = value.toString().split(".");
    if (!after) return value;
    return Number(`${before}.${after.substring(0, places)}`);
  };

  const formatRateDisplay = () => {
    if (!exchangeRate || !fromCurrency || !toCurrency) return "";

    if (exchangeRate.displayRate === "1") {
      const inverseRate = clampDecimals(1 / exchangeRate.rate);
      return `${toCurrency.symbol}1 = ${fromCurrency.symbol}${FormatNumber(
        inverseRate
      )}`;
    }

    const cleanRate = clampDecimals(parseFloat(exchangeRate.displayRate));
    return `${fromCurrency.symbol}1 = ${toCurrency.symbol}${FormatNumber(
      cleanRate
    )}`;
  };

  const calculateGrossAmount = () => {
    if (!exchangeRate || !amount || !toCurrency) return 0;

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum)) return 0;

    return amountNum * exchangeRate.rate;
  };

  const calculateAmountToReceive = () => {
    if (!exchangeRate || !amount || !toCurrency) return "0.00";

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum)) return "0.00";

    const grossAmount = calculateGrossAmount();
    const amountAfterFee = grossAmount - fee;

    const formattedAmount = FormatNumber(to2DecimalPlace(amountAfterFee));
    return `${toCurrency.symbol} ${formattedAmount}`;
  };

  // ---- Fixed useEffect for fetching fee

  // useEffect(() => {
  //   const fetchFee = async () => {
  //     setFee(0);

  //     // Only fetch fee if we have all required info for fee-eligible flows
  //     if (
  //       !paymentDetails.debit.paymentMethod ||
  //       !paymentDetails.credit.paymentMethod ||
  //       !amount ||
  //       parseFloat(amount) <= 0 ||
  //       !fromCurrency ||
  //       !toCurrency ||
  //       !exchangeRate // Make sure exchange rate is available
  //     ) {
  //       return;
  //     }

  //     // Check if this is a fee-eligible flow
  //     const isFeeEligible =
  //       paymentDetails.debit.paymentMethod === PAYMENT_METHOD.WALLET &&
  //       (paymentDetails.credit.paymentMethod === PAYMENT_METHOD.MOBILE_MONEY ||
  //         paymentDetails.credit.paymentMethod === PAYMENT_METHOD.BANK_TRANSFER);

  //     if (!isFeeEligible) {
  //       return;
  //     }

  //     const grossAmount = calculateGrossAmount();
  //     if (grossAmount <= 0) {
  //       return;
  //     }

  //     setIsLoadingFee(true);

  //     try {
  //       // const getFee = await fetchExchangeFee(grossAmount, toCurrency.iso2);
  //       const getFee = await fetchTransactionFee(grossAmount, toCurrency.iso2, CONFIG_TYPE.WITHDRAWAL_FEE);

  //       setFee(getFee || 0);
  //     } catch (error) {
  //       console.error("Error fetching fee:", error);
  //       setFee(0);
  //     } finally {
  //       setIsLoadingFee(false);
  //     }
  //   };

  //   const debounceTimer = setTimeout(fetchFee, 300);
  //   return () => clearTimeout(debounceTimer);
  // }, [
  //   paymentDetails.debit.paymentMethod,
  //   paymentDetails.credit.paymentMethod,
  //   amount,
  //   fromCurrency,
  //   toCurrency,
  //   exchangeRate,
  // ]);

  const showFee = () => {
    if (fee > 0) {
      return `${toCurrency?.symbol} ${FormatNumber(to2DecimalPlace(fee))}`;
    }

    if (isLoadingFee) {
      return <Spinner className="h-3 w-3 text-blue-500" />;
    }

    return "Free";
  };

const handleFromCurrencySelect = (currency: Currency) => {
  const wasGHSWallet = 
    fromCurrency?.code === "GHS" && 
    paymentDetails.debit.paymentMethod === PAYMENT_METHOD.WALLET;
  
  const isNowGHSWallet = 
    currency.code === "GHS" && 
    paymentDetails.debit.paymentMethod === PAYMENT_METHOD.WALLET;
  
  setFromCurrency(currency);
  setShowFromDropdown(false);
  setPayFrom("");
  setAccountNameError("");
  setFromCurrencySearch("");
  setPaymentDetails((prev) => ({
    ...prev,
    debit: emptyPaymentDetails,
  }));
  
  if ((wasGHSWallet || isNowGHSWallet) && 
      paymentDetails.credit.paymentMethod !== PAYMENT_METHOD.WALLET) {
    setReceiveTo("");
    setPaymentDetails((prev) => ({
      ...prev,
      credit: emptyPaymentDetails,
    }));
  }
};

  // ---- handles currency to when selected
  const handleToCurrencySelect = (currency: Currency) => {
    setToCurrency(currency);
    setShowToDropdown(false);
    setReceiveTo("");
    setToCurrencySearch("");
    setPaymentDetails((prev) => ({
      ...prev,
      credit: emptyPaymentDetails,
    }));
  };

  if (loadingCurrency) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800 h-[300px] flex items-center justify-center">
        <Spinner className="h-10 w-10 text-blue-500" />
      </div>
    );
  }

  return (
    <>
      <div
        className={`rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800 transition-all duration-300 ${
          showDetails ? "h-auto" : "h-[300px]"
        }`}
      >
        <div className="px-6 py-4 bg-white rounded-t-xl dark:bg-gray-900">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
            Exchange
          </h3>
        </div>

        <div className="p-6 bg-white rounded-b-xl dark:bg-gray-900">
          {error && (
            <div className="p-3 mb-4 text-red-700 bg-red-100 border border-red-400 rounded-md">
              {error}
            </div>
          )}
          {apiMessage && !showDetails && (
            <div className="p-3 mb-4 text-red-700 bg-red-100 border border-red-400 rounded-md">
              {apiMessage}
            </div>
          )}

          <div className="flex gap-4 mb-4" ref={dropdownRef}>
            <div className="flex-1 relative">
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                I have
              </label>
              <div className="relative">
                <button
                  type="button"
                  className="w-full px-4 py-3 pr-10 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white flex items-center"
                  onClick={() => {
                    setShowFromDropdown(!showFromDropdown);
                    setShowToDropdown(false);
                    setFromCurrencySearch("");
                    setToCurrencySearch("");
                  }}
                >
                  {fromCurrency && (
                    <>
                      <img
                        src={fromCurrency.flag}
                        alt={fromCurrency.code}
                        className="w-5 h-5 mr-2"
                      />
                      <span>{fromCurrency.name}</span>
                    </>
                  )}
                  <ChevronDownIcon className="ml-auto h-5 w-5 text-gray-400" />
                </button>

                {showFromDropdown && (
                  <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md text-base ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:border dark:border-gray-700">
                    <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                      <input
                        type="text"
                        autoFocus
                        placeholder="Search currency..."
                        value={fromCurrencySearch}
                        onChange={(e) => setFromCurrencySearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="max-h-52 overflow-auto py-1">
                      {(() => {
                        const search = fromCurrencySearch.trim().toLowerCase();
                        const filtered = currencies.filter(
                          (c) =>
                            !search || c.name.toLowerCase().includes(search),
                        );
                        return filtered.length === 0 ? (
                          <p className="px-4 py-2 text-sm text-gray-400">
                            No results
                          </p>
                        ) : (
                          filtered.map((currency) => (
                            <div
                              key={`${currency.code}-${currency.iso2}`}
                              className={`flex items-center px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer ${
                                fromCurrency?.code === currency.code
                                  ? "bg-gray-100 dark:bg-gray-700"
                                  : ""
                              }`}
                              onClick={() => handleFromCurrencySelect(currency)}
                            >
                              <img
                                src={currency.flag}
                                alt={currency.code}
                                className="w-5 h-5 mr-2"
                              />
                              <span>{currency.name}</span>
                            </div>
                          ))
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 relative">
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                I want
              </label>
              <div className="relative">
                <button
                  type="button"
                  className="w-full px-4 py-3 pr-10 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white flex items-center"
                  onClick={() => {
                    setShowToDropdown(!showToDropdown);
                    setShowFromDropdown(false);
                    setFromCurrencySearch("");
                    setToCurrencySearch("");
                  }}
                >
                  {toCurrency && (
                    <>
                      <img
                        src={toCurrency.flag}
                        alt={toCurrency.code}
                        className="w-5 h-5 mr-2"
                      />
                      <span>{toCurrency.name}</span>
                    </>
                  )}
                  <ChevronDownIcon className="ml-auto h-5 w-5 text-gray-400" />
                </button>

                {showToDropdown && (
                  <div className="absolute z-10 mt-1 w-full bg-white shadow-lg rounded-md text-base ring-1 ring-black ring-opacity-5 focus:outline-none dark:bg-gray-800 dark:border dark:border-gray-700">
                    <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                      <input
                        type="text"
                        autoFocus
                        placeholder="Search currency..."
                        value={toCurrencySearch}
                        onChange={(e) => setToCurrencySearch(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div className="max-h-52 overflow-auto py-1">
                      {(() => {
                        const search = toCurrencySearch.trim().toLowerCase();
                        const filtered = currencies.filter(
                          (c) =>
                            !search || c.name.toLowerCase().includes(search),
                        );
                        return filtered.length === 0 ? (
                          <p className="px-4 py-2 text-sm text-gray-400">
                            No results
                          </p>
                        ) : (
                          filtered.map((currency) => (
                            <div
                              key={`${currency.code}-${currency.iso2}`}
                              className={`flex items-center px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer ${
                                toCurrency?.code === currency.code
                                  ? "bg-gray-100 dark:bg-gray-700"
                                  : ""
                              }`}
                              onClick={() => handleToCurrencySelect(currency)}
                            >
                              <img
                                src={currency.flag}
                                alt={currency.code}
                                className="w-5 h-5 mr-2"
                              />
                              <span>{currency.name}</span>
                            </div>
                          ))
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm text-gray-500 mb-1 dark:text-gray-400">
              Enter amount to change
            </label>
            <div className="relative">
              <div
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-2xl font-semibold min-w-[50px] whitespace-nowrap`}
              >
                {currencySymbol()}
              </div>
              <input
                type="text"
                className={`w-full ${
                  currencySymbol().length > 1 ? "pl-16" : "pl-10"
                } pr-4 py-3 text-2xl font-semibold text-gray-900 bg-transparent border-b border-gray-200 focus:ring-0 focus:border-blue-500 dark:text-white dark:border-gray-700`}
                placeholder="0.00"
                value={
                  amount ? new Intl.NumberFormat().format(Number(amount)) : ""
                }
                onChange={handleAmountChange}
              />
              {isLoading && (
                <div className="absolute right-0 top-1/2 transform -translate-y-1/2 p-2">
                  <Spinner className="h-6 w-6 text-blue-500" />
                </div>
              )}
            </div>
          </div>

          {showDetails && exchangeRate && (
            <>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Rate</span>
                  <span className="text-gray-600 dark:text-gray-300">
                    {formatRateDisplay()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">
                    Amount to receive
                  </span>
                  <span className="text-gray-600 dark:text-gray-300">
                    {calculateAmountToReceive()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">Fee</span>
                  <span className="text-gray-600 dark:text-gray-300">
                    {showFee()}
                  </span>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                    Pay from
                  </label>
                  {accountNameError && (
                    <div className="p-2 mb-2 text-red-700 bg-red-100 border border-red-400 rounded-md text-sm">
                      {accountNameError}
                    </div>
                  )}
                  <button
                    onClick={handlePayFromClick}
                    className="w-full px-4 py-3 text-left text-gray-700 bg-gray-50 border border-gray-200 rounded-xl hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:border-blue-500"
                  >
                    {payFrom || "Select payment method"}
                    <ChevronDownIcon className="float-right h-5 w-5 text-gray-400" />
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                    Receive to
                  </label>
                  <button
                    onClick={handleReceiveToClick}
                    className="w-full px-4 py-3 text-left text-gray-700 bg-gray-50 border border-gray-200 rounded-xl hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white dark:hover:border-blue-500"
                  >
                    {receiveTo || "Select receiving method"}
                    <ChevronDownIcon className="float-right h-5 w-5 text-gray-400" />
                  </button>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                  Narration{" "}
                  <span className="text-gray-400 font-normal">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={narration}
                  onChange={(e) => setNarration(e.target.value)}
                  className="w-full px-4 py-3 text-gray-700 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                  placeholder="Enter narration (optional)"
                />
              </div>

              <button
                className={`w-full py-3 px-4 text-white font-medium rounded-lg transition duration-200 ${
                  hasInsufficientBalance ||
                  !payFrom ||
                  !receiveTo ||
                  isLoading ||
                  !!apiMessage ||
                  !!accountNameError
                    ? "bg-[#EB2E67]/70 dark:bg-[#EB2E67]/50 cursor-not-allowed"
                    : "bg-[#EB2E67] hover:bg-[#EB2E67]/90 dark:bg-[#EB2E67] dark:hover:bg-[#EB2E67]/90"
                }`}
                disabled={
                  hasInsufficientBalance ||
                  !payFrom ||
                  !receiveTo ||
                  isLoading ||
                  !!apiMessage ||
                  !!accountNameError
                }
                onClick={handleContinue}
              >
                {isLoading ? (
                  <Spinner className="h-5 w-5 mx-auto" />
                ) : hasInsufficientBalance ? (
                  "Insufficient Balance"
                ) : (
                  "Continue"
                )}
              </button>
            </>
          )}
        </div>

        {/* Method Type Selection Modal */}
        <SelectMethodTypeModal
          isOpen={showMethodTypeModal}
          onClose={() => setShowMethodTypeModal(false)}
          onSelect={handleMethodTypeSelect}
          title={methodTypeContext === "PAY" ? "Pay From" : "Receive To"}
          currency={
            methodTypeContext === "PAY"
              ? fromCurrency?.code || ""
              : toCurrency?.code || ""
          }
          availableMethods={
            methodTypeContext === "PAY" ? finalPayMethods : finalReceiveMethods
          }
          feeApplies={feeApplies}
        />

        {/* Wallet Selection Modal */}
        <WalletSelectionModal
          isOpen={showWalletModal}
          onClose={() => setShowWalletModal(false)}
          onSelect={handleWalletSelect}
          currency={
            methodTypeContext === "PAY"
              ? fromCurrency?.code || ""
              : toCurrency?.code || ""
          }
          amount={methodTypeContext === "PAY" ? parseFloat(amount) || 0 : 0}
        />

        {/* Payment Modals */}
        {fromCurrency && exchangeRate && (
          <PayBankDetailsModal
            isOpen={showPayModal}
            onClose={() => setShowPayModal(false)}
            onSave={handleSavePayDetails}
            currency={fromCurrency.code}
            hasBankList={fromCurrency?.hasBankList ?? false}
            paymentMethod={
              paymentDetails.debit.paymentMethod ||
              (Array.isArray(exchangeRate.acceptedPaymentMethod)
                ? exchangeRate.acceptedPaymentMethod[0]
                : exchangeRate.acceptedPaymentMethod)
            }
            exchangeRate={exchangeRate}
            fromCurrency={fromCurrency}
            compareSelected={paymentDetails.debit}
            allowedMomoProviders={exchangeRate.allowedMomoProviders}
          />
        )}

        {toCurrency && exchangeRate && (
          <ReceiveBankDetailsModal
            isOpen={showReceiveModal}
            onClose={() => setShowReceiveModal(false)}
            onSave={handleSaveReceiveDetails}
            hasBankList={toCurrency?.hasBankList ?? false}
            currency={toCurrency.code}
            paymentMethod={
              paymentDetails.credit.paymentMethod ||
              (Array.isArray(exchangeRate.payoutPaymentMethod)
                ? exchangeRate.payoutPaymentMethod[0]
                : exchangeRate.payoutPaymentMethod)
            }
            exchangeRate={exchangeRate}
            toCurrency={toCurrency}
            compareSelected={paymentDetails.credit}
            allowedMomoProviders={exchangeRate.allowedMomoProviders}
            payFromMethod={paymentDetails.debit.paymentMethod}
          />
        )}

        <ConfirmationModal
          isOpen={showConfirmationModal}
          onClose={() => setShowConfirmationModal(false)}
          amount={amount}
          timeLeft={timeLeft}
          order={order}
          buyerDebitPaymentMethod={
            paymentDetails.debit.paymentMethod as PAYMENT_METHOD
          }
          // buyerCreditPaymentMethod={paymentDetails.credit.paymentMethod as PAYMENT_METHOD}
        />

        <AutoProcessingModal
          isOpen={showAutoProcessingModal}
          onClose={() => setShowAutoProcessingModal(false)}
          orderId={order?.id ?? ""}
        />
      </div>
    </>
  );
}
